/**
 * SEEDS Platform API utilities for BrickLab
 */

const SEEDS_API_BASE = process.env.SEEDS_URL || 'http://localhost:3000';

let authToken = null;

/**
 * Initialize auth from URL parameters
 * Call this once when Scratch loads
 * @returns {Promise<object>} Response with success and workspaceId
 */
const initAuthFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    authToken = params.get('token');
    const workspaceId = params.get('workspaceId');
    const classId = params.get('classId');
    const isSharedWorkspace = params.get('isSharedWorkspace');

    return {
        token: authToken,
        workspaceId,
        classId,
        isSharedWorkspace
    };
};

/**
 * Get headers with auth token
 * @param {string | null} contentType - Unique workspace identifier
 * @returns {Promise<object>} Response with success and workspaceId
 */
const getAuthHeaders = (contentType = null) => {
    const headers = {};
    if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
    }
    if (contentType) {
        headers['Content-Type'] = contentType;
    }
    return headers;
};

/**
 * Save project .sb3 to SEEDS backend
 * @param {string} workspaceId - Unique workspace identifier
 * @param {string} workspaceName - Project name
 * @param {Blob} sb3Blob - The .sb3 file blob
 * @returns {Promise<object>} Response with success and workspaceId
 */
const saveProjectToSEEDS = async (workspaceId, workspaceName, sb3Blob) => {
    const formData = new FormData();
    formData.append('workspaceId', workspaceId);
    formData.append('workspaceName', workspaceName);
    formData.append('sb3', sb3Blob, `${workspaceId}.sb3`);

    const response = await fetch(`${SEEDS_API_BASE}/api/blockly/workspaces`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
    });

    if (!response.ok) {
        throw new Error(`Failed to save project: ${response.status}`);
    }

    return response.json();
};

/**
 * Load project from SEEDS backend
 * @param {string} workspaceId - Workspace identifier
 * @param {string | null | undefined} classId - Workspace identifier
 * @returns {Promise<object>} Workspace data with sb3Url and workspaceName
 */
const loadProjectFromSEEDS = async (workspaceId, classId) => {
    const response = await fetch(
        `${SEEDS_API_BASE}/api/blockly/workspaces/${workspaceId}${classId ? `?classId=${classId}` : ''
        }`,
        {
            headers: getAuthHeaders()
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to load project: ${response.status}`);
    }

    const data = await response.json();
    return data.workspace;
};

/**
 * List user's workspaces
 * @returns {Promise<Array>} Array of workspace objects
 */
const listWorkspaces = async () => {
    const response = await fetch(`${SEEDS_API_BASE}/api/blockly/workspaces`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) {
        throw new Error(`Failed to list workspaces: ${response.status}`);
    }

    const data = await response.json();
    return data.workspaces;
};

/**
 * Delete a workspace
 * @param {string} workspaceId - Workspace identifier
 * @returns {Promise<object>} Response with success boolean
 */
const deleteWorkspace = async workspaceId => {
    const response = await fetch(`${SEEDS_API_BASE}/api/blockly/workspaces`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ workspaceId })
    });

    if (!response.ok) {
        throw new Error(`Failed to delete workspace: ${response.status}`);
    }

    return response.json();
};

/**
 * Fetch all BrickLab global images grouped by category
 * @param {boolean} includeSignedUrls - If true, returns signed URLs directly
 * @returns {Promise<object>} Response with categories and images
 */
const fetchBrickLabGlobalImages = async (includeSignedUrls = false) => {
    const url = `${SEEDS_API_BASE}/api/bricklab/images${includeSignedUrls ? '?includeSignedUrls=true' : ''}`;

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // Important for CORS with credentials
        headers: getAuthHeaders('application/json')
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch images: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Batch convert S3 keys to signed URLs
 * @param {string[]} s3Keys - Array of S3 keys
 * @returns {Promise<object>} Response with signed URLs
 */
const getSignedUrlsForKeys = async s3Keys => {
    const url = `${SEEDS_API_BASE}/api/utils/signed-url`;

    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders('application/json'),
        body: JSON.stringify({ keys: s3Keys })
    });

    if (!response.ok) {
        throw new Error(`Failed to get signed URLs: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Get current BrickLab user (for scratch-gui: role, userId, classId)
 * @returns {Promise<object>} { success, userId, role, email, classId }
 */
const getBrickLabMe = async () => {
    const response = await fetch(`${SEEDS_API_BASE}/api/bricklab/me`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders()
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to get user');
    }
    return data;
};

/**
 * Get teacher's classes (for dropdowns / multi-select)
 * @returns {Promise<object>} { success, classes: [{ id, subject, grade, name }] }
 */
const getTeacherClasses = async () => {
    const response = await fetch(`${SEEDS_API_BASE}/api/bricklab/me/classes`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders()
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to get classes');
    }
    return data;
};

/**
 * Upload images to a BrickLab category
 * @param {string} categoryId - Category id
 * @param {Array<{file: File, name: string}>} files - Array of { file, name }
 * @returns {Promise<object>} { success, message, images }
 */
const uploadBrickLabImages = async (categoryId, files) => {
    const formData = new FormData();
    formData.append('categoryId', categoryId);
    files.forEach(({ file, name }) => {
        formData.append('file', file);
        formData.append('name', name);
    });
    const response = await fetch(`${SEEDS_API_BASE}/api/bricklab/images`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(), // Do not set Content-Type; browser sets multipart boundary
        body: formData
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to upload images');
    }
    return data;
};

/**
 * Assign images to multiple classes (bulk)
 * @param {string[]} classIds - Class ids
 * @param {string[]} imageIds - Image ids
 * @returns {Promise<object>} { success, message, assignedCount, skippedCount }
 */
const assignBrickLabImagesToClasses = async (classIds, imageIds) => {
    const response = await fetch(`${SEEDS_API_BASE}/api/classes/teacher/bricklab-images/assign`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders('application/json'),
        body: JSON.stringify({ classIds, imageIds })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to assign images');
    }
    return data;
};

/**
 * Get images assigned to a class
 * @param {string} classId - Class id
 * @returns {Promise<object>} { success, images: [...] }
 */
const getClassBrickLabImages = async (classId) => {
    const response = await fetch(`${SEEDS_API_BASE}/api/classes/teacher/${classId}/bricklab-images`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders()
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch class images');
    }
    return data;
};

/**
 * Remove an image from a class
 * @param {string} classId - Class id
 * @param {string} imageId - Image id
 * @returns {Promise<object>} { success, message }
 */
const removeBrickLabImageFromClass = async (classId, imageId) => {
    const response = await fetch(
        `${SEEDS_API_BASE}/api/classes/teacher/${classId}/bricklab-images/${imageId}`,
        {
            method: 'DELETE',
            credentials: 'include',
            headers: getAuthHeaders()
        }
    );
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to remove image');
    }
    return data;
};

export {
    initAuthFromURL,
    saveProjectToSEEDS,
    loadProjectFromSEEDS,
    listWorkspaces,
    deleteWorkspace,
    fetchBrickLabGlobalImages,
    getSignedUrlsForKeys,
    getBrickLabMe,
    getTeacherClasses,
    uploadBrickLabImages,
    assignBrickLabImagesToClasses,
    getClassBrickLabImages,
    removeBrickLabImageFromClass
};
